import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, LogOut, Plus, Search, ShieldCheck, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Logo } from '@/components/ui/Logo'
import { cn } from '@/lib/utils'
import {
  adminAccessService,
  AdminUnauthorizedError,
  type AppType,
  type IntegrationEmail,
} from '@/services/adminAccess.service'

const inputClassName =
  'w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors'
const labelClassName = 'text-[11px] font-medium uppercase tracking-wider text-muted-foreground'

const ITEMS_PER_PAGE = 20

const APP_TABS: { key: AppType | 'all'; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'calux', label: 'Zoni Calux' },
  { key: 'sigma', label: 'Zoni Sigma' },
  { key: 'aihakim', label: 'AI Hakim' },
]

const APP_BADGE: Record<string, { label: string; className: string }> = {
  calux: { label: 'Zoni Calux', className: 'bg-violet-500/10 text-violet-400' },
  sigma: { label: 'Zoni Sigma', className: 'bg-sky-500/10 text-sky-400' },
  aihakim: { label: 'AI Hakim', className: 'bg-amber-500/10 text-amber-400' },
}

type View = 'login' | '2fa' | 'panel'

interface Feedback {
  type: 'success' | 'error'
  text: string
}

export function AdminAccessPage() {
  const [view, setView] = useState<View>(adminAccessService.hasSession() ? 'panel' : 'login')
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  // ── Login state ──
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [twoFACode, setTwoFACode] = useState('')
  // Challenge assinado emitido pelo /login (TTL 5min) — substitui o userId cru
  const [pendingChallenge, setPendingChallenge] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  // ── Painel (CRUD) ──
  const [items, setItems] = useState<IntegrationEmail[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<AppType | 'all'>('all')
  const [hasAppColumn, setHasAppColumn] = useState(false)

  const [showAdd, setShowAdd] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addUserId, setAddUserId] = useState('')
  const [addApp, setAddApp] = useState<AppType>('calux')
  const [adding, setAdding] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState<IntegrationEmail | null>(null)
  const [deleting, setDeleting] = useState(false)

  const feedbackTimer = useRef<number | null>(null)

  function showFeedback(type: Feedback['type'], text: string) {
    setFeedback({ type, text })
    if (feedbackTimer.current !== null) window.clearTimeout(feedbackTimer.current)
    feedbackTimer.current = window.setTimeout(() => setFeedback(null), 4000)
  }

  function handleSessionExpired(err: unknown): boolean {
    if (err instanceof AdminUnauthorizedError) {
      setView('login')
      setAuthError('Sessão administrativa expirada — faça login novamente')
      return true
    }
    return false
  }

  // ── Auth handlers ──
  // Os endpoints /access-control/* exigem super_admin|admin; o login do
  // admin-backend também aceita 'support' — barrar aqui evita entrar num
  // painel onde tudo responde 403.
  function ensureAllowedRole(result: { user?: { role: string } }): boolean {
    if (result.user && !['super_admin', 'admin'].includes(result.user.role)) {
      adminAccessService.logout()
      setAuthError('Seu perfil não tem permissão para gerenciar acessos')
      return false
    }
    return true
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || !password) {
      setAuthError('Informe e-mail e senha')
      return
    }
    setAuthLoading(true)
    setAuthError(null)
    try {
      const result = await adminAccessService.login(email.trim(), password)
      if (result.requiresSetup2FA) {
        setAuthError('Sua conta exige configuração de 2FA — configure primeiro pelo painel admin')
        return
      }
      if (result.requires2FA) {
        if (!result.challengeToken) {
          setAuthError('Resposta inesperada do servidor')
          return
        }
        setPendingChallenge(result.challengeToken)
        setTwoFACode('')
        setView('2fa')
        return
      }
      if (result.token) {
        if (!ensureAllowedRole(result)) return
        setPassword('')
        setView('panel')
        return
      }
      setAuthError('Resposta inesperada do servidor')
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Falha no login')
    } finally {
      setAuthLoading(false)
    }
  }

  async function handleVerify2FA(e: React.FormEvent) {
    e.preventDefault()
    if (!pendingChallenge) {
      setView('login')
      setAuthError('Sessão de verificação expirada — faça login novamente')
      return
    }
    if (twoFACode.trim().length < 6) {
      setAuthError('Informe o código de 6 dígitos')
      return
    }
    setAuthLoading(true)
    setAuthError(null)
    try {
      const result = await adminAccessService.verify2FA(pendingChallenge, twoFACode.trim())
      if (result.token) {
        if (!ensureAllowedRole(result)) {
          setView('login')
          return
        }
        setPassword('')
        setTwoFACode('')
        setPendingChallenge(null)
        setView('panel')
        return
      }
      setAuthError('Código inválido')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha na verificação'
      // Challenge expirado/inválido (TTL 5min): o código certo não vai mais
      // passar — volta pro login em vez de deixar o usuário preso no 2FA.
      if (/sessão de verificação/i.test(message)) {
        setPendingChallenge(null)
        setView('login')
      }
      setAuthError(message)
    } finally {
      setAuthLoading(false)
    }
  }

  function handleLogout() {
    adminAccessService.logout()
    setView('login')
  }

  // ── CRUD ──
  // Busca digitada ≠ busca aplicada: trocar de tab/página usa só o filtro
  // já submetido, nunca texto pendente do input.
  const [appliedSearch, setAppliedSearch] = useState('')
  // Guarda de sequência: respostas fora de ordem (tab trocada durante um
  // fetch lento) não podem sobrescrever o estado da requisição mais nova.
  const loadSeq = useRef(0)

  const loadData = useCallback(
    async (p: number, q: string, tab: AppType | 'all') => {
      const seq = ++loadSeq.current
      setLoading(true)
      try {
        const data = await adminAccessService.listEmails({
          page: p,
          limit: ITEMS_PER_PAGE,
          q: q || undefined,
          app: tab !== 'all' ? tab : undefined,
        })
        if (seq !== loadSeq.current) return
        setItems(data.items)
        setTotal(data.total)
        setHasAppColumn(data.hasAppColumn)
      } catch (err) {
        if (seq !== loadSeq.current) return
        if (!handleSessionExpired(err)) showFeedback('error', 'Erro ao carregar emails autorizados')
      } finally {
        if (seq === loadSeq.current) setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (view !== 'panel') return
    // Re-login: zera filtros/página para o estado corresponder ao fetch
    setActiveTab('all')
    setSearch('')
    setAppliedSearch('')
    setPage(1)
    loadData(1, '', 'all')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  function handleTabChange(tab: AppType | 'all') {
    setActiveTab(tab)
    setPage(1)
    loadData(1, appliedSearch, tab)
  }

  function handleSearch() {
    setAppliedSearch(search)
    setPage(1)
    loadData(1, search, activeTab)
  }

  function handlePrev() {
    if (page <= 1) return
    const p = page - 1
    setPage(p)
    loadData(p, appliedSearch, activeTab)
  }

  function handleNext() {
    if (page * ITEMS_PER_PAGE >= total) return
    const p = page + 1
    setPage(p)
    loadData(p, appliedSearch, activeTab)
  }

  async function handleAdd() {
    if (!addEmail.trim() || !addUserId.trim()) {
      showFeedback('error', 'Preencha todos os campos')
      return
    }
    const userId = Number(addUserId)
    if (!userId || userId <= 0) {
      showFeedback('error', 'ID do usuário inválido')
      return
    }
    setAdding(true)
    try {
      await adminAccessService.addEmail({ user_id: userId, email: addEmail.trim(), app: addApp })
      showFeedback('success', 'Email autorizado adicionado')
      setShowAdd(false)
      setAddEmail('')
      setAddUserId('')
      setAddApp('calux')
      setPage(1)
      loadData(1, appliedSearch, activeTab)
    } catch (err) {
      if (!handleSessionExpired(err)) {
        showFeedback('error', err instanceof Error ? err.message : 'Erro ao adicionar email')
      }
    } finally {
      setAdding(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await adminAccessService.removeEmail(deleteTarget.id)
      showFeedback('success', 'Email removido')
      setDeleteTarget(null)
      // Se era o único item de uma página > 1, volta uma página em vez de
      // exibir uma página órfã vazia.
      const targetPage = items.length === 1 && page > 1 ? page - 1 : page
      setPage(targetPage)
      loadData(targetPage, appliedSearch, activeTab)
    } catch (err) {
      if (!handleSessionExpired(err)) showFeedback('error', 'Erro ao remover email')
    } finally {
      setDeleting(false)
    }
  }

  // ── Views de autenticação ──
  if (view === 'login' || view === '2fa') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-sm border border-border rounded-xl">
          <CardHeader>
            <div className="flex items-center gap-2 mb-1">
              <Logo size="sm" />
            </div>
            <CardTitle className="flex items-center gap-2 text-foreground text-base font-semibold">
              <ShieldCheck className="size-4 text-muted-foreground" />
              Gestão de Acessos
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Área administrativa — entre com suas credenciais de administrador
            </p>
          </CardHeader>
          <CardContent>
            {view === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className={labelClassName}>E-mail</label>
                  <input
                    type="email"
                    autoComplete="username"
                    className={inputClassName}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelClassName}>Senha</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    className={inputClassName}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {authError && <p className="text-xs text-red-400">{authError}</p>}
                <Button type="submit" className="w-full" disabled={authLoading}>
                  {authLoading && <Loader2 className="size-4 animate-spin mr-2" />}
                  Entrar
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="space-y-1.5">
                  <label className={labelClassName}>Código de autenticação (2FA)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    className={cn(inputClassName, 'text-center tracking-[0.4em] font-mono')}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                  />
                </div>
                {authError && <p className="text-xs text-red-400">{authError}</p>}
                <Button type="submit" className="w-full" disabled={authLoading}>
                  {authLoading && <Loader2 className="size-4 animate-spin mr-2" />}
                  Verificar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setPendingChallenge(null)
                    setView('login')
                  }}
                >
                  Voltar
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Painel ──
  const colSpan = hasAppColumn ? 6 : 5

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Feedback */}
        {feedback && (
          <div
            className={cn(
              'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-xs font-medium shadow-lg',
              feedback.type === 'success'
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-red-500/15 text-red-400 border border-red-500/30'
            )}
          >
            {feedback.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <ShieldCheck className="size-5 text-muted-foreground" />
              <h1 className="text-lg font-semibold text-foreground tracking-tight">Controle de Acessos</h1>
            </div>
            <p className="text-xs text-muted-foreground">
              Gerencie emails autorizados para login via integração por app
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="gap-1.5" onClick={() => setShowAdd(true)}>
              <Plus className="size-3.5" />
              Novo
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={handleLogout} title="Sair">
              <LogOut className="size-3.5" />
              Sair
            </Button>
          </div>
        </div>

        {/* App tabs */}
        <div className="flex items-center gap-2">
          <div className="h-9 p-1 bg-muted/50 rounded-lg inline-flex gap-1">
            {APP_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={cn(
                  'h-7 px-3 rounded-md text-xs font-medium transition-all flex items-center justify-center',
                  activeTab === tab.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por email, nome ou email do usuário..."
              className={cn(inputClassName, 'h-8 text-xs pl-8')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button size="sm" variant="outline" onClick={handleSearch}>
            Buscar
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Email autorizado</th>
                  {hasAppColumn && (
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">App</th>
                  )}
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Usuário</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">ID</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Data</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-12 text-center">
                      <Loader2 className="size-5 animate-spin text-muted-foreground mx-auto" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-4 py-12 text-center text-muted-foreground">
                      Nenhum email autorizado encontrado
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const badge = item.app ? APP_BADGE[item.app] : null
                    return (
                      <tr key={item.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 text-foreground font-medium">{item.email}</td>
                        {hasAppColumn && (
                          <td className="px-4 py-2.5">
                            {badge ? (
                              <span
                                className={cn(
                                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                                  badge.className
                                )}
                              >
                                {badge.label}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-2.5">
                          <div className="flex flex-col">
                            <span className="text-foreground">{item.user_name || '—'}</span>
                            <span className="text-muted-foreground text-[10px]">{item.user_email || ''}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground font-mono">{item.user_id}</td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {item.created_at
                            ? new Date(item.created_at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            onClick={() => setDeleteTarget(item)}
                            className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Remover"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {total > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Mostrando {(page - 1) * ITEMS_PER_PAGE + 1}–{Math.min(page * ITEMS_PER_PAGE, total)} de {total}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={handlePrev} disabled={page <= 1}>
                Anterior
              </Button>
              <Button size="sm" variant="outline" onClick={handleNext} disabled={page * ITEMS_PER_PAGE >= total}>
                Próximo
              </Button>
            </div>
          </div>
        )}

        {/* Modal: Adicionar */}
        {showAdd && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowAdd(false)
            }}
          >
            <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">Adicionar email autorizado</h2>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className={labelClassName}>ID do Usuário</label>
                  <input
                    type="number"
                    placeholder="Ex: 1234"
                    className={cn(inputClassName, 'h-8 text-xs')}
                    value={addUserId}
                    onChange={(e) => setAddUserId(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelClassName}>Email autorizado</label>
                  <input
                    type="email"
                    placeholder="email@exemplo.com"
                    className={cn(inputClassName, 'h-8 text-xs')}
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className={labelClassName}>App</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAddApp('calux')}
                      className={cn(
                        'h-8 px-4 rounded-md text-xs font-medium ring-1 ring-white/[0.06]',
                        addApp === 'calux'
                          ? 'bg-violet-500/10 text-violet-400'
                          : 'bg-background text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Zoni Calux
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddApp('sigma')}
                      className={cn(
                        'h-8 px-4 rounded-md text-xs font-medium ring-1 ring-white/[0.06]',
                        addApp === 'sigma'
                          ? 'bg-sky-500/10 text-sky-400'
                          : 'bg-background text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Zoni Sigma
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddApp('aihakim')}
                      className={cn(
                        'h-8 px-4 rounded-md text-xs font-medium ring-1 ring-white/[0.06]',
                        addApp === 'aihakim'
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'bg-background text-muted-foreground hover:text-foreground'
                      )}
                    >
                      AI Hakim
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancelar
                </Button>
                <Button size="sm" className="gap-1.5" onClick={handleAdd} disabled={adding}>
                  {adding && <Loader2 className="size-3 animate-spin" />}
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Confirmar exclusão */}
        {deleteTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setDeleteTarget(null)
            }}
          >
            <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground">Confirmar exclusão</h2>
              <p className="text-xs text-muted-foreground">
                Remover o email <strong className="text-foreground">{deleteTarget.email}</strong> do app{' '}
                <strong className="text-foreground">
                  {APP_BADGE[deleteTarget.app ?? 'calux']?.label ?? 'Zoni Calux'}
                </strong>{' '}
                da lista de acessos autorizados?
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={() => setDeleteTarget(null)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting && <Loader2 className="size-3 animate-spin" />}
                  Remover
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
